import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search, Shield, Award, Building2, HandCoins, ScrollText, TrendingUp, Sparkles, CheckCircle2, MapPin, Star } from "lucide-react";
import { COMPANY, CATEGORIES, SERVICES, TESTIMONIALS, STATS, WHATSAPP_URL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchProperties, fetchStats } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1613977257363-707ba9348227?w=1920&q=90", // luxury villa
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=90", // modern home
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=90", // interior
  "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=1920&q=90", // skyline
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ total_properties: 500, cities_covered: 8, happy_clients: 500, years_experience: 12 });
  const [purpose, setPurpose] = useState("buy");
  const [category, setCategory] = useState("all");
  const [city, setCity] = useState("");
  const [heroIdx, setHeroIdx] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    fetchProperties({ featured: true, limit: 6 }).then(setFeatured).catch(() => {});
    fetchStats().then(setStats).catch(() => {});
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(id);
  }, []);

  const doSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (purpose && purpose !== "all") params.set("purpose", purpose);
    if (category && category !== "all") params.set("category", category);
    if (city) params.set("city", city);
    nav(`/properties?${params.toString()}`);
  };

  return (
    <div className="pt-0">
      <SEO title="Buy, Sell & Rent Property in Indore" description="Verified plots, villas, offices, warehouses & residential properties in Indore and Pan India. Buy, sell, rent with trust — Wilshire Buildcon Co." path="/" />
      {/* Hero */}
      <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden bg-brand-navy">
        {/* Cycling background images */}
        <div className="absolute inset-0">
          {HERO_IMAGES.map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out ${i === heroIdx ? "opacity-45 hero-ken" : "opacity-0"}`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/85 to-brand-navy/40" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,169,97,0.35),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(201,169,97,0.20),transparent_60%)]" />
          <div className="absolute inset-0 grain opacity-30" />
        </div>

        {/* Decorative floating blobs */}
        <div className="absolute top-20 -left-20 w-72 h-72 rounded-full bg-brand-gold/20 blur-3xl animate-float-slow pointer-events-none" />
        <div className="absolute bottom-40 right-10 w-96 h-96 rounded-full bg-brand-gold/10 blur-3xl animate-float-slow pointer-events-none" style={{ animationDelay: "2s" }} />

        {/* Sparkles */}
        <Sparkles className="absolute top-32 right-1/4 w-4 h-4 text-brand-gold/60 animate-pulse" />
        <Sparkles className="absolute top-1/2 left-1/3 w-3 h-3 text-brand-gold/40 animate-pulse" style={{ animationDelay: "1s" }} />
        <Sparkles className="absolute bottom-40 left-1/4 w-5 h-5 text-brand-gold/50 animate-pulse" style={{ animationDelay: "1.5s" }} />

        {/* Corner ornaments */}
        <div className="absolute top-24 right-8 hidden lg:block">
          <div className="w-24 h-24 border-t-2 border-r-2 border-brand-gold/40" />
        </div>
        <div className="absolute bottom-40 left-8 hidden lg:block">
          <div className="w-24 h-24 border-b-2 border-l-2 border-brand-gold/40" />
        </div>

        <div className="relative z-10 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-8 items-center pt-24 pb-40">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-brand-gold/30 px-4 py-2 rounded-full">
              <Star size={14} className="text-brand-gold fill-brand-gold" />
              <span className="text-xs tracking-[0.25em] uppercase text-brand-gold font-bold">Wilshire Buildcon · Est. 2012</span>
            </div>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white leading-[1.02] tracking-tight text-balance drop-shadow-2xl max-w-3xl">
              Where <em className="text-brand-gold not-italic relative inline-block">timeless value<span className="absolute -bottom-2 left-0 w-full h-1 bg-brand-gold/40 blur-sm" /></em><br />meets <span className="italic font-light">modern living</span>
            </h1>
            <p className="mt-6 text-brand-gold/95 text-base sm:text-lg max-w-xl font-medium leading-relaxed">
              India's trusted name in property.
            </p>
            <p className="mt-2 text-white/85 text-base sm:text-lg max-w-xl leading-relaxed">
              Verified plots, villas, offices and industrial spaces — with complete transparency from search to registry.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/properties">
                <Button data-testid="hero-browse-btn" className="btn-gold rounded-sm h-12 px-7 shadow-xl shadow-brand-gold/20">
                  Browse Properties <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <a href={WHATSAPP_URL()} target="_blank" rel="noreferrer">
                <Button data-testid="hero-whatsapp-btn" variant="outline" className="rounded-sm h-12 px-7 border-white/60 text-white bg-white/5 backdrop-blur-sm hover:bg-white hover:text-brand-navy">
                  Chat on WhatsApp
                </Button>
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-white">
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-gold" /> <span className="text-white">500+ </span><span className="text-brand-gold">Verified Listings</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-gold" /> <span className="text-white">RERA</span> <span className="text-brand-gold">Compliant</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-gold" /> <span className="text-white">Pan India</span> <span className="text-brand-gold">Reach</span></div>
            </div>
          </div>

          {/* Floating property preview card */}
          <div className="lg:col-span-5 hidden lg:block animate-fade-up" style={{ animationDelay: "400ms" }}>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-brand-gold/30 to-transparent blur-2xl -z-10" />
              <div className="bg-white/95 backdrop-blur-md border border-brand-gold/40 shadow-2xl overflow-hidden animate-float-slow">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={HERO_IMAGES[heroIdx]} alt="" className="w-full h-full object-cover transition-all duration-700" />
                  <div className="absolute top-3 left-3 bg-brand-gold text-brand-navy text-[10px] font-bold px-3 py-1 uppercase tracking-widest">Featured</div>
                  <div className="absolute bottom-3 right-3 bg-brand-navy/90 backdrop-blur text-white text-[10px] font-bold px-3 py-1 uppercase tracking-widest flex items-center gap-1"><Star size={10} className="fill-brand-gold text-brand-gold" /> Premium</div>
                </div>
                <div className="p-5">
                  <div className="text-brand-gold font-serif text-3xl leading-none">₹ 2.25 Cr</div>
                  <div className="mt-2 font-serif text-lg text-brand-navy">Luxury 4BHK Villa</div>
                  <div className="mt-1 flex items-center gap-1 text-xs text-brand-charcoal/70"><MapPin size={12} className="text-brand-gold" /> Bicholi Mardana, Indore</div>
                  <div className="mt-4 flex items-center justify-between text-xs text-brand-charcoal border-t border-brand-gold/20 pt-3">
                    <span>4 BHK</span>
                    <span>5 Baths</span>
                    <span>4,500 sqft</span>
                  </div>
                </div>
              </div>
              {/* Small floating rating card */}
              <div className="absolute -bottom-6 -left-6 bg-brand-navy border border-brand-gold/40 shadow-xl p-4 flex items-center gap-3 animate-float-slow" style={{ animationDelay: "1s" }}>
                <div className="text-brand-gold text-2xl">★★★★★</div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-gold">Client Rating</div>
                  <div className="text-white font-serif text-lg leading-none">4.9 / 5</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search bar */}
        <form onSubmit={doSearch} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 w-[calc(100%-2rem)] max-w-5xl bg-white/95 backdrop-blur-md border border-brand-gold/40 shadow-2xl p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <Select value={purpose} onValueChange={setPurpose}>
            <SelectTrigger data-testid="hero-search-purpose" className="rounded-sm h-11"><SelectValue placeholder="Purpose" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="buy">Buy</SelectItem>
              <SelectItem value="sell">Sell</SelectItem>
              <SelectItem value="rent">Rent</SelectItem>
              <SelectItem value="lease">Lease</SelectItem>
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="hero-search-category" className="rounded-sm h-11"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="City (e.g. Indore)" value={city} onChange={(e) => setCity(e.target.value)} data-testid="hero-search-city" className="rounded-sm h-11 md:col-span-2" />
          <Button type="submit" data-testid="hero-search-btn" className="rounded-sm h-11 bg-brand-navy hover:bg-brand-navy/90 text-brand-gold">
            <Search size={16} className="mr-2" /> Search
          </Button>
        </form>
      </section>

      {/* Trust marquee band */}
      <section className="bg-brand-navy border-y border-brand-gold/30 overflow-hidden">
        <div className="marquee-track flex gap-16 py-4 whitespace-nowrap">
          {[...Array(2)].map((_, r) => (
            <div key={r} className="flex gap-16 items-center shrink-0">
              {["RERA Compliant", "★ Verified Listings", "12+ Years of Trust", "Pan India Reach", "24×7 Support", "Legal Documentation", "Transparent Deals", "Ethical Practices"].map((t, i) => (
                <div key={i} className="flex items-center gap-3 text-brand-gold font-serif text-xl tracking-wide">
                  <span className="w-2 h-2 bg-brand-gold rotate-45" /> {t}
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-y border-brand-gold/20 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: `${stats.total_properties}+`, label: "Verified Properties" },
            { value: `${stats.cities_covered}+`, label: "Cities Covered" },
            { value: `${stats.happy_clients}+`, label: "Happy Clients" },
            { value: `${stats.years_experience}+`, label: "Years of Trust" },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 100}>
              <div>
                <div className="font-serif text-5xl lg:text-6xl text-brand-navy">{s.value}</div>
                <div className="mt-1 text-[11px] tracking-[0.3em] uppercase text-brand-gold font-bold">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-10 max-w-7xl mx-auto">
        <Reveal>
          <div className="text-center mb-14">
            <div className="divider-gold">Explore</div>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-brand-navy">Property Categories</h2>
            <p className="mt-3 text-brand-charcoal/70 max-w-2xl mx-auto">Residential, commercial, industrial or agricultural — we cover every category with verified listings.</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.key} delay={i * 40}>
              <Link
                to={`/properties?category=${c.key}`}
                data-testid={`category-${c.key}`}
                className="group relative block aspect-[4/5] overflow-hidden bg-brand-navy"
              >
                <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-110 transition-all duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/40 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-brand-gold">Category</div>
                  <div className="mt-1 font-serif text-xl text-white">{c.label}</div>
                  <div className="mt-2 flex items-center gap-1 text-xs text-brand-gold opacity-0 group-hover:opacity-100 transition-opacity">Explore <ArrowRight size={12} /></div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Featured properties */}
      <section className="bg-brand-cream/50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
              <div>
                <div className="divider-gold">Handpicked</div>
                <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-brand-navy">Featured Properties</h2>
              </div>
              <Link to="/properties" data-testid="home-view-all-btn" className="text-sm text-brand-navy hover:text-brand-gold flex items-center gap-2 group">
                View all listings <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 60}><PropertyCard p={p} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* About MD */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 bg-brand-gold/10 -z-10" />
              <img src={COMPANY.mdPhoto} alt={COMPANY.mdName} className="w-full aspect-[4/5] object-cover object-top" />
              <div className="absolute bottom-6 left-6 bg-brand-navy text-white px-6 py-4 shadow-xl">
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-gold">Leadership</div>
                <div className="mt-1 font-serif text-xl">{COMPANY.mdName}</div>
                <div className="text-xs text-brand-ivory/70">{COMPANY.mdTitle}</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div>
              <div className="divider-gold">Meet Our Founder</div>
              <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-brand-navy leading-tight">
                A vision built on <em className="text-brand-gold not-italic">trust & transparency</em>
              </h2>
              <p className="mt-5 text-brand-charcoal/80 leading-relaxed">
                Mr. B. S. Rajput leads Wilshire Buildcon Co. with an unwavering commitment to ethical, transparent property transactions. Under his guidance, the company has become a preferred partner for property owners, investors, businesses and developers across India.
              </p>
              <p className="mt-3 text-brand-charcoal/80 leading-relaxed">
                From verified listings and legal diligence to end-to-end deal closure, every engagement is anchored in professionalism and long-term relationships.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                {["Trusted Leadership", "Pan India Vision", "Commercial Expertise", "Transparent Practices"].map((x) => (
                  <div key={x} className="flex items-center gap-2 text-brand-navy"><span className="w-1.5 h-1.5 bg-brand-gold" /> {x}</div>
                ))}
              </div>
              <div className="mt-8 flex gap-3">
                <Link to="/about"><Button data-testid="home-about-btn" className="bg-brand-navy text-brand-gold rounded-sm h-11 hover:bg-brand-navy/90">Read Our Story</Button></Link>
                <a href={WHATSAPP_URL(`Hello Mr. B.S. Rajput, I would like to schedule a consultation.`)} target="_blank" rel="noreferrer">
                  <Button data-testid="home-consult-btn" variant="outline" className="rounded-sm h-11 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-brand-gold">Book Consultation</Button>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services */}
      <section className="bg-brand-navy text-brand-ivory py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="text-center mb-14">
              <div className="divider-gold">What We Do</div>
              <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-white">End-to-End Property Solutions</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 50}>
                <div className="p-6 border border-brand-gold/20 hover:border-brand-gold hover:bg-brand-gold/5 transition-all group h-full">
                  <div className="w-11 h-11 flex items-center justify-center border border-brand-gold/40 text-brand-gold mb-4 group-hover:bg-brand-gold group-hover:text-brand-navy transition-colors">
                    {s.icon === "Home" && <Building2 size={18} />}
                    {s.icon === "Tag" && <HandCoins size={18} />}
                    {s.icon === "Key" && <Shield size={18} />}
                    {s.icon === "FileCheck" && <ScrollText size={18} />}
                    {s.icon === "TrendingUp" && <TrendingUp size={18} />}
                    {s.icon === "Calculator" && <Award size={18} />}
                    {s.icon === "Building2" && <Building2 size={18} />}
                    {s.icon === "Briefcase" && <Award size={18} />}
                  </div>
                  <h3 className="font-serif text-xl text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-brand-ivory/70 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="py-20 lg:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <div className="text-center mb-14">
            <div className="divider-gold">Why Wilshire</div>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-brand-navy">Trust that translates to results</h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { t: "Pan India Reach", d: "Property solutions across all major cities and states." },
            { t: "Verified Listings", d: "Every property is verified with clear title & documentation." },
            { t: "Transparent Process", d: "No hidden fees. Complete legal & registry assistance." },
            { t: "End-to-End Service", d: "From sourcing to registry — one partner, zero hassle." },
            { t: "Commercial & Industrial", d: "Specialised expertise in offices, warehouses & factories." },
            { t: "12+ Years Legacy", d: "Trusted by owners, investors, developers & enterprises." },
          ].map((x, i) => (
            <Reveal key={x.t} delay={i * 60}>
              <div className="bg-white p-8 border border-brand-gold/20 hover:shadow-md transition-shadow h-full">
                <div className="text-4xl font-serif text-brand-gold">0{i + 1}</div>
                <h3 className="mt-3 font-serif text-xl text-brand-navy">{x.t}</h3>
                <p className="mt-2 text-sm text-brand-charcoal/70">{x.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-brand-cream/50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="text-center mb-14">
              <div className="divider-gold">Voices of Trust</div>
              <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-brand-navy">Client Testimonials</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 60}>
                <div className="bg-white p-6 border border-brand-gold/20 h-full">
                  <div className="text-brand-gold text-2xl leading-none">★★★★★</div>
                  <p className="mt-4 text-sm text-brand-charcoal/80 italic leading-relaxed">"{t.quote}"</p>
                  <div className="mt-5 pt-4 border-t border-brand-gold/20">
                    <div className="font-serif text-lg text-brand-navy">{t.name}</div>
                    <div className="text-xs text-brand-charcoal/60">{t.city}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-10">
        <div className="max-w-5xl mx-auto bg-brand-navy text-white p-12 lg:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 grain" />
          <div className="relative">
            <div className="divider-gold">Ready to move?</div>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl">Your next address is one call away</h2>
            <p className="mt-4 text-brand-ivory/80 max-w-2xl mx-auto">Speak to our property advisors for a personalised, no-obligation consultation.</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <a href={`tel:${COMPANY.phone}`}><Button data-testid="cta-call-btn" className="btn-gold rounded-sm h-12 px-7">Call {COMPANY.phone}</Button></a>
              <Link to="/contact"><Button data-testid="cta-contact-btn" variant="outline" className="rounded-sm h-12 px-7 border-brand-gold text-brand-gold bg-transparent hover:bg-brand-gold hover:text-brand-navy">Contact Us</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
